/**
 * Animation orchestrator — managed requestAnimationFrame loops.
 *
 * Provides `createAnimationLoop` (general-purpose) and `createFlashLoop`
 * (time-limited with progress 0→1) to eliminate rAF boilerplate in main.ts.
 * Accepts an injectable RAFProvider for deterministic unit testing.
 */

/** Injectable rAF pair — production uses window, tests use fakes. */
export interface RAFProvider {
  requestAnimationFrame(cb: FrameRequestCallback): number;
  cancelAnimationFrame(id: number): void;
  now(): number;
}

/**
 * Return value from the frame callback:
 * - `true` → schedule another frame
 * - `false` → stop the loop (self-terminate)
 */
export type FrameResult = boolean;

/**
 * User-supplied frame logic. Receives dt (ms since last frame)
 * and the raw timestamp. Returns whether to continue.
 */
export type FrameCallback = (dt: number, now: number) => FrameResult;

/** Opaque handle to a managed animation loop. */
export interface AnimationLoop {
  /** Start the loop (no-op if already running). */
  start(): void;
  /** Force-stop the loop externally (cancels pending rAF). */
  stop(): void;
  /** True while the loop has a pending rAF scheduled. */
  readonly running: boolean;
}

/**
 * Convenience: a time-limited flash loop.
 * Calls `onProgress(progress: 0→1, now)` each frame, auto-stops at end.
 */
export interface FlashLoop extends AnimationLoop {
  /** Restart the flash from t=0 (useful for re-triggering). */
  restart(): void;
}

const defaultRAF: RAFProvider = {
  requestAnimationFrame: (cb) => window.requestAnimationFrame(cb),
  cancelAnimationFrame: (id) => window.cancelAnimationFrame(id),
  now: () => performance.now(),
};

/** Factory: create a managed animation loop. */
export function createAnimationLoop(
  onFrame: FrameCallback,
  raf: RAFProvider = defaultRAF,
): AnimationLoop {
  let rafId: number | null = null;
  let lastTime: number | null = null;

  function frame(now: number): void {
    const dt = lastTime !== null ? now - lastTime : 0;
    lastTime = now;
    const shouldContinue = onFrame(dt, now);
    if (shouldContinue) {
      rafId = raf.requestAnimationFrame(frame);
    } else {
      rafId = null;
      lastTime = null;
    }
  }

  return {
    start() {
      if (rafId !== null) return;
      lastTime = raf.now();
      rafId = raf.requestAnimationFrame(frame);
    },
    stop() {
      if (rafId !== null) {
        raf.cancelAnimationFrame(rafId);
        rafId = null;
      }
      lastTime = null;
    },
    get running() {
      return rafId !== null;
    },
  };
}

/** Factory: create a time-limited flash loop that auto-stops after `durationMs`. */
export function createFlashLoop(
  durationMs: number,
  onProgress: (progress: number, now: number) => void,
  onComplete?: () => void,
  raf: RAFProvider = defaultRAF,
): FlashLoop {
  let startTime: number | null = null;

  const loop = createAnimationLoop((_, now) => {
    if (startTime === null) startTime = now;
    const elapsed = now - startTime;
    if (elapsed >= durationMs) {
      startTime = null;
      onComplete?.();
      return false;
    }
    onProgress(elapsed / durationMs, now);
    return true;
  }, raf);

  return {
    start() { startTime = raf.now(); loop.start(); },
    stop() { loop.stop(); startTime = null; },
    get running() { return loop.running; },
    restart() {
      loop.stop();
      startTime = null;
      startTime = raf.now();
      loop.start();
    },
  };
}
